import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import socket from '../services/socket';
import logo from '../assets/logo.png';
import '../styles/Header.css';

// ── NotificationBell ──────────────────────────────────────────────────────────
const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount]     = useState(0);
    const [open, setOpen]                   = useState(false);
    const wrapRef = useRef(null);

    // Initial fetch
    useEffect(() => {
        api.get('/notifications')
            .then(({ data }) => {
                setNotifications(data.notifications || []);
                setUnreadCount(data.unreadCount    || 0);
            })
            .catch(() => {});
    }, []);

    // Real-time: prepend incoming notification, bump badge
    useEffect(() => {
        const handleNew = (notification) => {
            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
        };
        socket.on('new_notification', handleNew);
        return () => socket.off('new_notification', handleNew);
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const markRead = async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            setNotifications(prev =>
                prev.map(n => n._id === id ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch {}
    };

    return (
        <div className="notif-wrap" ref={wrapRef}>
            <button
                className="notif-trigger"
                onClick={() => setOpen(o => !o)}
                aria-label="Notifications"
                aria-expanded={open}
            >
                {/* Bell icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span className="notif-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="notif-dropdown" role="dialog" aria-label="Notifications panel">
                    <div className="notif-dropdown-header">
                        <span>Notifications</span>
                        {unreadCount > 0 && (
                            <span className="notif-new-pill">{unreadCount} new</span>
                        )}
                    </div>

                    <div className="notif-list">
                        {notifications.length === 0 ? (
                            <p className="notif-empty">You're all caught up</p>
                        ) : (
                            notifications.map(n => (
                                <button
                                    key={n._id}
                                    className={`notif-item${!n.isRead ? ' notif-item--unread' : ''}`}
                                    onClick={() => !n.isRead && markRead(n._id)}
                                >
                                    <div className="notif-item-top">
                                        {!n.isRead && <span className="notif-dot" />}
                                        <p className="notif-msg">{n.message}</p>
                                    </div>
                                    <time className="notif-time">
                                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                                    </time>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Header ────────────────────────────────────────────────────────────────────
const Header = () => {
    const { user, logout } = useContext(AuthContext) || {};
    const [menuOpen, setMenuOpen] = useState(false);

    // Connect socket when user authenticates; disconnect on logout / unmount
    useEffect(() => {
        if (!user) return;

        socket.connect();
        socket.emit('register', user.user._id);

        return () => {
            socket.disconnect();
        };
    }, [user]);

    // Close mobile menu on route change (window resize guard)
    useEffect(() => {
        const close = () => setMenuOpen(false);
        window.addEventListener('resize', close);
        return () => window.removeEventListener('resize', close);
    }, []);

    const userName   = user?.user?.name || '';
    const initial    = (userName || '?')[0].toUpperCase();
    const closeMenu  = () => setMenuOpen(false);

    const handleLogout = () => {
        closeMenu();
        logout();
    };

    return (
        <header className="header">
            <nav className="header-nav">

                {/* Logo */}
                <Link to="/" className="header-logo-link" onClick={closeMenu}>
                    <img src={logo} alt="Comrades" className="header-logo" />
                </Link>

                {/* Hamburger (mobile only) */}
                <button
                    className={`hamburger${menuOpen ? ' hamburger--open' : ''}`}
                    onClick={() => setMenuOpen(o => !o)}
                    aria-label="Toggle navigation"
                    aria-expanded={menuOpen}
                >
                    <span /><span /><span />
                </button>

                {/* Nav links — flex row on desktop, dropdown on mobile */}
                <ul className={`header-links${menuOpen ? ' header-links--open' : ''}`}>
                    {user ? (
                        <>
                            <li>
                                <Link to="/feed" onClick={closeMenu}>Feed</Link>
                            </li>
                            <li>
                                <Link to="/create-post" onClick={closeMenu}>Create Post</Link>
                            </li>
                            <li>
                                <NotificationBell />
                            </li>
                            <li>
                                <Link to="/profile" className="avatar-link" onClick={closeMenu} title={userName}>
                                    <span className="avatar">{initial}</span>
                                </Link>
                            </li>
                            <li>
                                <button className="logout-btn" onClick={handleLogout}>
                                    Logout
                                </button>
                            </li>
                        </>
                    ) : (
                        <>
                            <li>
                                <Link to="/login" className="nav-pill nav-pill--ghost" onClick={closeMenu}>
                                    Login
                                </Link>
                            </li>
                            <li>
                                <Link to="/register" className="nav-pill nav-pill--filled" onClick={closeMenu}>
                                    Register
                                </Link>
                            </li>
                        </>
                    )}
                </ul>

            </nav>
        </header>
    );
};

export default Header;
